// src/pages/PlanDetailsPage.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { createSubscriptionPlan, updateSubscriptionPlan } from "../../redux/slices/subscriptionSlice";
import { fetchCountries } from "../../redux/slices/countriesSlice";
import ToggleSwitch from "../../components/Common/ToggleSwitch";
import { useTranslation } from "../../contexts/LanguageContext";

function PlanDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { token } = useSelector((state) => state.auth);
  const { countries } = useSelector((state) => state.countries);
  
  // For discount fields
  const [discountEnabled, setDiscountEnabled] = useState(false);

  // Check if we're editing an existing plan (passed via navigation state)
  const editingPlan = location.state?.plan || null;

  // Initialize form data – if editingPlan exists, use it; otherwise, use default empty values.
  const [formData, setFormData] = useState(
    editingPlan || {
      planName: "",
      description: "",
      visibility: "public",
      isPlanActive: false,
      maxAssistantAccountsAllowed: "",
      identityVerificationRequired: false,
      allowedVerificationTypes: [],
      planPricing: {
        countryId: "",
        monthlyPrice: "",
        yearlyPrice: "",
        commissionPercentage: "",
        escrowDurationDays: "",
        monthlyDiscountedPrice: "",
        yearlyDiscountedPrice: "",
        discountValidUntil: "",
      },
      planFeatures: {
        clips: {
          isFeatureEnabled: false,
          maxClipPerMonth: "",
          maxClipVideoSizeMB: "",
          maxClipDurationSeconds: "",
          maxClipPrice: "",
          maxLotsPerClip: "",
          maxQuantityPerClip: "",
          maxClipStorageDays: "",
        },
        auctions: {
          isFeatureEnabled: false,
          maxLotsPerAuction: "",
          maxAuctionPrice: "",
          maxBidIncrement: "",
          maxAuctionDurationMinutes: "",
        },
        channels: {
          isFeatureEnabled: false,
          maxProductsPerChannel: "",
          maxChannelDurationMinute: "",
          maxChannelPrice: "",
        },
      },
    }
  );

  // Set discountEnabled based on existing plan data
  useEffect(() => {
    if (editingPlan && editingPlan.planPricing) {
      const hasDiscountData = editingPlan.planPricing.monthlyDiscountedPrice > 0 || 
                              editingPlan.planPricing.yearlyDiscountedPrice > 0 ||
                              editingPlan.planPricing.discountValidUntil;
      setDiscountEnabled(hasDiscountData);
    }
  }, [editingPlan]);

  // Fetch countries on mount using Redux
  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);

  // Generic input change handler (supports nested keys via dot notation)
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes(".")) {
      const keys = name.split(".");
      setFormData((prev) => {
        const updated = { ...prev };
        let current = updated;
        keys.forEach((key, index) => {
          if (index === keys.length - 1) {
            current[key] = type === "checkbox" ? checked : value;
          } else {
            current = current[key];
          }
        });
        return updated;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  // For allowed verification types checkboxes
  const handleVerificationTypeChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const current = prev.allowedVerificationTypes;
      return checked
        ? { ...prev, allowedVerificationTypes: [...current, value] }
        : { ...prev, allowedVerificationTypes: current.filter((v) => v !== value) };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Build base pricing object
    const basePricing = {
      countryId: formData.planPricing.countryId,
      monthlyPrice: Number(formData.planPricing.monthlyPrice),
      yearlyPrice: Number(formData.planPricing.yearlyPrice),
      commissionPercentage: Number(formData.planPricing.commissionPercentage),
      escrowDurationDays: Number(formData.planPricing.escrowDurationDays),
    };

    // Only include discount fields if discount is enabled
    if (discountEnabled) {
      basePricing.monthlyDiscountedPrice = Number(formData.planPricing.monthlyDiscountedPrice) || 0;
      basePricing.yearlyDiscountedPrice = Number(formData.planPricing.yearlyDiscountedPrice) || 0;
      basePricing.discountValidUntil = formData.planPricing.discountValidUntil || null;
    }

    // Build payload with proper type conversions
    const payload = {
      planName: formData.planName,
      description: formData.description,
      visibility: formData.visibility,
      isPlanActive: formData.isPlanActive,
      maxAssistantAccountsAllowed: Number(formData.maxAssistantAccountsAllowed),
      identityVerificationRequired: formData.identityVerificationRequired,
      allowedVerificationTypes: formData.allowedVerificationTypes,
      planPricing: basePricing,
      planFeatures: {
        clips: {
          isFeatureEnabled: formData.planFeatures.clips.isFeatureEnabled,
          maxClipPerMonth: Number(formData.planFeatures.clips.maxClipPerMonth) || 0,
          maxClipVideoSizeMB: Number(formData.planFeatures.clips.maxClipVideoSizeMB) || 0,
          maxClipDurationSeconds: Number(formData.planFeatures.clips.maxClipDurationSeconds) || 0,
          maxClipPrice: Number(formData.planFeatures.clips.maxClipPrice) || 0,
          maxLotsPerClip: Number(formData.planFeatures.clips.maxLotsPerClip) || 0,
          maxQuantityPerClip: Number(formData.planFeatures.clips.maxQuantityPerClip) || 0,
          maxClipStorageDays: Number(formData.planFeatures.clips.maxClipStorageDays) || 0,
        },
        auctions: {
          isFeatureEnabled: formData.planFeatures.auctions.isFeatureEnabled,
          maxLotsPerAuction: Number(formData.planFeatures.auctions.maxLotsPerAuction) || 0,
          maxAuctionPrice: Number(formData.planFeatures.auctions.maxAuctionPrice) || 0,
          maxBidIncrement: Number(formData.planFeatures.auctions.maxBidIncrement) || 0,
          maxAuctionDurationMinutes: Number(formData.planFeatures.auctions.maxAuctionDurationMinutes) || 0,
        },
        channels: {
          isFeatureEnabled: formData.planFeatures.channels.isFeatureEnabled,
          maxProductsPerChannel: Number(formData.planFeatures.channels.maxProductsPerChannel) || 0,
          maxChannelDurationMinute: Number(formData.planFeatures.channels.maxChannelDurationMinute) || 0,
          maxChannelPrice: Number(formData.planFeatures.channels.maxChannelPrice) || 0,
        },
      },
    };

    if (editingPlan) {
      // Update existing plan
      dispatch(updateSubscriptionPlan({ planId: editingPlan._id, planData: payload }))
        .unwrap()
        .then((data) => {
          console.log("Plan updated successfully:", data);
          navigate("/subscriptions");
        })
        .catch((error) => {
          console.error("Failed to update subscription plan:", error);
        });
    } else {
      // Create new plan
      dispatch(createSubscriptionPlan(payload))
        .unwrap()
        .then((data) => {
          console.log("Plan created successfully:", data);
          navigate("/subscriptions");
        })
        .catch((error) => {
          console.error("Failed to create subscription plan:", error);
        });
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8 bg-white shadow-md rounded-md">
      <h1 className="font-semibold text-2xl text-themeDark mb-4">
        {editingPlan ? t("planForm.editTitle") : t("planForm.addTitle")}
      </h1>
      <form onSubmit={handleSubmit}>
        {/* Plan Details Section */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">{t("planForm.planDetails")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="planName" className="block text-sm font-medium text-gray-700">
                {t("planForm.planName")}
              </label>
              <input
                type="text"
                id="planName"
                name="planName"
                value={formData.planName}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                required
              />
            </div>
            <div>
              <label htmlFor="visibility" className="block text-sm font-medium text-gray-700">
                {t("planForm.visibility")}
              </label>
              <select
                id="visibility"
                name="visibility"
                value={formData.visibility}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
              >
                <option value="public">{t("planForm.visibility.public")}</option>
                <option value="private">{t("planForm.visibility.private")}</option>
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPlanActive"
                name="isPlanActive"
                checked={formData.isPlanActive}
                onChange={handleInputChange}
                className="mr-2"
              />
              <label htmlFor="isPlanActive" className="text-sm text-gray-700">
                {t("planForm.activePlan")}
              </label>
            </div>
            <div>
              <label htmlFor="maxAssistantAccountsAllowed" className="block text-sm font-medium text-gray-700">
                {t("planForm.maxAssistants")}
              </label>
              <input
                type="number"
                id="maxAssistantAccountsAllowed"
                name="maxAssistantAccountsAllowed"
                value={formData.maxAssistantAccountsAllowed}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                {t("planForm.description")}
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                {t("planForm.identityVerificationRequired")}
              </label>
              <label htmlFor="identityVerificationRequired" className="flex items-center mt-1">
                <input
                  type="checkbox"
                  id="identityVerificationRequired"
                  name="identityVerificationRequired"
                  checked={formData.identityVerificationRequired}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">{t("planForm.yes")}</span>
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                {t("planForm.allowedVerificationTypes")}
              </label>
              <div className="mt-1 flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="personal"
                    checked={formData.allowedVerificationTypes.includes("personal")}
                    onChange={handleVerificationTypeChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{t("planForm.personal")}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="company"
                    checked={formData.allowedVerificationTypes.includes("company")}
                    onChange={handleVerificationTypeChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{t("planForm.company")}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Plan Pricing Section */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">{t("planForm.planPricing")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="planPricing.countryId" className="block text-sm font-medium text-gray-700">
                {t("planForm.country")}
              </label>
              <select
                id="planPricing.countryId"
                name="planPricing.countryId"
                value={formData.planPricing.countryId}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                required
              >
                <option value="">{t("planForm.selectCountry")}</option>
                {countries.map((country) => (
                  <option key={country._id} value={country._id}>
                    {country.countryName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="planPricing.commissionPercentage" className="block text-sm font-medium text-gray-700">
                {t("planForm.commissionPercentage")}
              </label>
              <input
                type="number"
                id="planPricing.commissionPercentage"
                name="planPricing.commissionPercentage"
                value={formData.planPricing.commissionPercentage}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
              />
            </div>
            <div>
              <label htmlFor="planPricing.monthlyPrice" className="block text-sm font-medium text-gray-700">
                {t("planForm.monthlyPrice")}
              </label>
              <input
                type="number"
                step="0.01"
                id="planPricing.monthlyPrice"
                name="planPricing.monthlyPrice"
                value={formData.planPricing.monthlyPrice}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
              />
            </div>
            <div>
              <label htmlFor="planPricing.yearlyPrice" className="block text-sm font-medium text-gray-700">
                {t("planForm.yearlyPrice")}
              </label>
              <input
                type="number"
                step="0.01"
                id="planPricing.yearlyPrice"
                name="planPricing.yearlyPrice"
                value={formData.planPricing.yearlyPrice}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
              />
            </div>
            <div>
              <label htmlFor="planPricing.escrowDurationDays" className="block text-sm font-medium text-gray-700">
                {t("planForm.escrowDuration")}
              </label>
              <input
                type="number"
                id="planPricing.escrowDurationDays"
                name="planPricing.escrowDurationDays"
                value={formData.planPricing.escrowDurationDays}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
              />
            </div>

            {/* Discount Toggle & Fields */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                {t("planForm.discountDetailsEnabled")}
              </label>
              <div className="mt-1">
                <input
                  type="checkbox"
                  id="discountEnabled"
                  name="discountEnabled"
                  checked={discountEnabled}
                  onChange={(e) => setDiscountEnabled(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">{t("planForm.enabled")}</span>
              </div>
            </div>

            {discountEnabled && (
              <>
                <div>
                  <label htmlFor="planPricing.monthlyDiscountedPrice" className="block text-sm font-medium text-gray-700">
                    {t("planForm.monthlyDiscountedPrice")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    id="planPricing.monthlyDiscountedPrice"
                    name="planPricing.monthlyDiscountedPrice"
                    value={formData.planPricing.monthlyDiscountedPrice}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                  />
                </div>
                <div>
                  <label htmlFor="planPricing.yearlyDiscountedPrice" className="block text-sm font-medium text-gray-700">
                    {t("planForm.yearlyDiscountedPrice")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    id="planPricing.yearlyDiscountedPrice"
                    name="planPricing.yearlyDiscountedPrice"
                    value={formData.planPricing.yearlyDiscountedPrice}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                  />
                </div>
                <div>
                  <label htmlFor="planPricing.discountValidUntil" className="block text-sm font-medium text-gray-700">
                    {t("planForm.discountValidUntil")}
                  </label>
                  <input
                    type="date"
                    id="planPricing.discountValidUntil"
                    name="planPricing.discountValidUntil"
                    value={formData.planPricing.discountValidUntil}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Plan Features Section */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">{t("planForm.planFeatures")}</h2>
          <div className="space-y-6">
            {/* Clips Section */}
            <div className="border border-gray-200 p-4 rounded-md">
              <h3 className="text-xl font-medium mb-2">{t("planForm.clips")}</h3>
              <div className="flex items-center mb-2">
                <ToggleSwitch
                  checked={formData.planFeatures.clips.isFeatureEnabled}
                  onChange={(newValue) => {
                    setFormData((prev) => {
                      const updated = { ...prev };
                      updated.planFeatures.clips.isFeatureEnabled = newValue;
                      return updated;
                    });
                  }}
                />
                <span className="ml-2 text-sm text-gray-700">{t("planForm.clipsFeature")}</span>
              </div>
              {formData.planFeatures.clips.isFeatureEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label htmlFor="planFeatures.clips.maxClipPerMonth" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxClipsMonth")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.clips.maxClipPerMonth"
                      id="planFeatures.clips.maxClipPerMonth"
                      value={formData.planFeatures.clips.maxClipPerMonth}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                  <div>
                    <label htmlFor="planFeatures.clips.maxClipVideoSizeMB" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxVideoSize")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.clips.maxClipVideoSizeMB"
                      id="planFeatures.clips.maxClipVideoSizeMB"
                      value={formData.planFeatures.clips.maxClipVideoSizeMB}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                  <div>
                    <label htmlFor="planFeatures.clips.maxClipDurationSeconds" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxDurationSec")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.clips.maxClipDurationSeconds"
                      id="planFeatures.clips.maxClipDurationSeconds"
                      value={formData.planFeatures.clips.maxClipDurationSeconds}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                  <div>
                    <label htmlFor="planFeatures.clips.maxClipPrice" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxClipPrice")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.clips.maxClipPrice"
                      id="planFeatures.clips.maxClipPrice"
                      value={formData.planFeatures.clips.maxClipPrice}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                  <div>
                    <label htmlFor="planFeatures.clips.maxLotsPerClip" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxLotsClip")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.clips.maxLotsPerClip"
                      id="planFeatures.clips.maxLotsPerClip"
                      value={formData.planFeatures.clips.maxLotsPerClip}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                  <div>
                    <label htmlFor="planFeatures.clips.maxQuantityPerClip" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxQuantityClip")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.clips.maxQuantityPerClip"
                      id="planFeatures.clips.maxQuantityPerClip"
                      value={formData.planFeatures.clips.maxQuantityPerClip}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                  <div>
                    <label htmlFor="planFeatures.clips.maxClipStorageDays" className="block text-sm font-medium text-gray-700">
                      {t("planForm.storageDays")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.clips.maxClipStorageDays"
                      id="planFeatures.clips.maxClipStorageDays"
                      value={formData.planFeatures.clips.maxClipStorageDays}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Auctions Section */}
            <div className="border border-gray-200 p-4 rounded-md">
              <h3 className="text-xl font-medium mb-2">{t("planForm.auctions")}</h3>
              <div className="flex items-center mb-2">
                <ToggleSwitch
                  checked={formData.planFeatures.auctions.isFeatureEnabled}
                  onChange={(newValue) => {
                    setFormData((prev) => {
                      const updated = { ...prev };
                      updated.planFeatures.auctions.isFeatureEnabled = newValue;
                      return updated;
                    });
                  }}
                />
                <span className="ml-2 text-sm text-gray-700">{t("planForm.auctionsFeature")}</span>
              </div>
              {formData.planFeatures.auctions.isFeatureEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label htmlFor="planFeatures.auctions.maxLotsPerAuction" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxLotsAuction")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.auctions.maxLotsPerAuction"
                      id="planFeatures.auctions.maxLotsPerAuction"
                      value={formData.planFeatures.auctions.maxLotsPerAuction}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                  <div>
                    <label htmlFor="planFeatures.auctions.maxAuctionPrice" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxAuctionPrice")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.auctions.maxAuctionPrice"
                      id="planFeatures.auctions.maxAuctionPrice"
                      value={formData.planFeatures.auctions.maxAuctionPrice}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                  <div>
                    <label htmlFor="planFeatures.auctions.maxBidIncrement" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxBidIncrement")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.auctions.maxBidIncrement"
                      id="planFeatures.auctions.maxBidIncrement"
                      value={formData.planFeatures.auctions.maxBidIncrement}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                  <div>
                    <label htmlFor="planFeatures.auctions.maxAuctionDurationMinutes" className="block text-sm font-medium text-gray-700">
                      {t("planForm.durationMinutes")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.auctions.maxAuctionDurationMinutes"
                      id="planFeatures.auctions.maxAuctionDurationMinutes"
                      value={formData.planFeatures.auctions.maxAuctionDurationMinutes}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Channels Section */}
            <div className="border border-gray-200 p-4 rounded-md">
              <h3 className="text-xl font-medium mb-2">{t("planForm.channels")}</h3>
              <div className="flex items-center mb-2">
                <ToggleSwitch
                  checked={formData.planFeatures.channels.isFeatureEnabled}
                  onChange={(newValue) => {
                    setFormData((prev) => {
                      const updated = { ...prev };
                      updated.planFeatures.channels.isFeatureEnabled = newValue;
                      return updated;
                    });
                  }}
                />
                <span className="ml-2 text-sm text-gray-700">{t("planForm.channelsFeature")}</span>
              </div>
              {formData.planFeatures.channels.isFeatureEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label htmlFor="planFeatures.channels.maxProductsPerChannel" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxProductsChannel")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.channels.maxProductsPerChannel"
                      id="planFeatures.channels.maxProductsPerChannel"
                      value={formData.planFeatures.channels.maxProductsPerChannel}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                  <div>
                    <label htmlFor="planFeatures.channels.maxChannelDurationMinute" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxDurationMinute")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.channels.maxChannelDurationMinute"
                      id="planFeatures.channels.maxChannelDurationMinute"
                      value={formData.planFeatures.channels.maxChannelDurationMinute}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                  <div>
                    <label htmlFor="planFeatures.channels.maxChannelPrice" className="block text-sm font-medium text-gray-700">
                      {t("planForm.maxChannelPrice")}
                    </label>
                    <input
                      type="number"
                      name="planFeatures.channels.maxChannelPrice"
                      id="planFeatures.channels.maxChannelPrice"
                      value={formData.planFeatures.channels.maxChannelPrice}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-themeOrange"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-themeOrange hover:bg-themeOrange-dark text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            {t("planForm.savePlan")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PlanDetailsPage;
